/**
 * Mascottes Kaizen — page brand identity (KAI + MEI).
 * Route admin : /app/mascottes
 *
 * Affiche les 2 mascottes officielles + assets téléchargeables + guidelines d'usage.
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Download, Sparkles, Copy } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const MASCOTS = [
  {
    name: "KAI",
    title: "Mascotte Kaizen (♂) — guerrier haïtien",
    subtitle: "Force martiale · sagesse · discipline · transformation",
    color: "amber",
    file: "asset_mascot_kai.jpg",
    persona_key: "narrateur_kaizen",
    persona_label: "Narrateur Kaizen (Marcus + voix Étienne Lefebvre FR pro)",
    use: "Identité brand générale · KAIZEN · cours en ligne · marketing dojo · Petits Samouraïs",
    pronunciation: "KAÏ",
    pronunciation_fr: "Se prononce « kah-ï » — comme dans haïku. Court, percutant, comme un kiaï.",
    pronunciation_en: "Pronounced 'KAI' (rhymes with sky/eye)",
    meaning: "« KAI » signifie en japonais : changement, transformation. Au cœur de Kaizen.",
    speech_text: "Bonjour, je m'appelle KAI. Je suis la mascotte officielle de KAIZEN. Mon nom signifie transformation en japonais.",
  },
  {
    name: "MEI",
    title: "Mascotte PowerKick Her (♀ blonde)",
    subtitle: "Empowerment · puissance féminine · communauté · confiance",
    color: "rose",
    file: "asset_mascot_mei.jpg",
    persona_key: "narratrice_powerkick",
    persona_label: "Narratrice PowerKick (Aubrey + voix Audrey FR pro)",
    use: "PowerKick Her · landing femmes 30-45 · marketing féminin",
    pronunciation: "MÉ-i",
    pronunciation_fr: "Se prononce « mé-i » — deux syllabes douces, en japonais signifie « beauté ».",
    pronunciation_en: "Pronounced 'MAY' (like the month)",
    meaning: "« MEI » signifie beauté en japonais. Force et grâce réunies.",
    speech_text: "Salut, moi c'est MEI. Je suis l'ambassadrice de PowerKick Her, le programme kick-boxing pour femmes du studio.",
  },
  {
    name: "LANA",
    title: "Mascotte féminine métisse (♀ Afro-Latina)",
    subtitle: "Diversité · inclusion · empowerment multi-origines",
    color: "purple",
    file: "asset_mascot_lana.jpg",
    persona_key: "narratrice_powerkick",
    persona_label: "(partage le porte-parole MEI) — option visuelle alternative",
    use: "Marketing inclusif · PowerKick Her diversity edition · Carnets multi-origines",
    pronunciation: "LA-na",
    pronunciation_fr: "Se prononce « LAH-na » — accent sur la première syllabe. Origine afro-latine.",
    pronunciation_en: "Pronounced 'LAH-na'",
    meaning: "« LANA » est une figure représentative de la diversité afro-latine de notre communauté.",
    speech_text: "Hola, je m'appelle LANA. Je représente la diversité afro-latine de la grande famille KAIZEN.",
  },
];


export default function MascottesModule() {
  const [personas, setPersonas] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/heygen/personas");
        setPersonas(r.data.personas);
      } catch { /* silencieux */ }
    })();
  }, []);

  const copyId = (val) => {
    navigator.clipboard.writeText(val);
    toast.success("Copié dans le presse-papier");
  };

  return (
    <div className="space-y-8 p-6 max-w-7xl mx-auto" data-testid="mascottes-module">
      <div>
        <h1 className="text-3xl font-bold text-amber-100 tracking-tight flex items-center gap-2">
          <Sparkles className="w-7 h-7 text-amber-400" /> Mascottes Kaizen
        </h1>
        <p className="text-zinc-400 text-sm mt-1">
          Identité visuelle officielle. <strong>KAI</strong> pour KAIZEN général · <strong>MEI</strong> pour PowerKick Her.
          Utilisées comme logo brand. Vidéos parlantes via porte-parole HeyGen pro.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {MASCOTS.map(m => {
          const persona = personas.find(p => p.persona_key === m.persona_key);
          const colorClasses = m.color === "amber"
            ? "border-amber-500/40 from-amber-500/10 to-zinc-950 shadow-amber-500/20"
            : m.color === "purple"
            ? "border-purple-500/40 from-purple-500/10 to-zinc-950 shadow-purple-500/20"
            : "border-rose-500/40 from-rose-500/10 to-zinc-950 shadow-rose-500/20";
          const badgeClass = m.color === "amber"
            ? "bg-amber-500 text-zinc-950"
            : m.color === "purple"
            ? "bg-purple-500 text-zinc-950"
            : "bg-rose-500 text-zinc-950";

          return (
            <div key={m.name} className={`rounded-3xl border-2 bg-gradient-to-br ${colorClasses} shadow-2xl overflow-hidden`} data-testid={`mascot-${m.name.toLowerCase()}`}>
              <div className="relative">
                <img src={`${BACKEND}/api/marketing-assets/file/${m.file}`}
                  alt={m.name}
                  className="w-full aspect-square object-cover" />
                <div className={`absolute top-4 left-4 px-4 py-1.5 rounded-full ${badgeClass} font-black text-sm tracking-[0.3em]`}>
                  {m.name}
                </div>
              </div>
              <div className="p-5 space-y-3">
                <div>
                  <h2 className={`text-xl font-bold ${m.color === "amber" ? "text-amber-100" : m.color === "purple" ? "text-purple-100" : "text-rose-100"}`}>
                    {m.title}
                  </h2>
                  <p className="text-xs text-zinc-400 mt-1">{m.subtitle}</p>
                </div>
                <div className="text-xs text-zinc-300 bg-zinc-950/60 rounded-lg p-3 border border-zinc-800">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Usage brand</div>
                  {m.use}
                </div>

                {persona && (
                  <div className="text-xs text-zinc-300 bg-zinc-950/60 rounded-lg p-3 border border-zinc-800">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Porte-parole HeyGen associé</div>
                    <div className={m.color === "amber" ? "text-amber-200" : m.color === "purple" ? "text-purple-200" : "text-rose-200"}>
                      🎤 {m.persona_label}
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-1 font-mono">
                      avatar: {persona.avatar_id?.substring(0, 30)}...
                    </div>
                  </div>
                )}

                <div className="text-xs text-zinc-300 bg-zinc-950/60 rounded-lg p-3 border border-zinc-800">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">📢 Comment prononcer</div>
                  <div className="flex items-center gap-2">
                    <span className={`text-2xl font-black ${m.color === "amber" ? "text-amber-300" : m.color === "purple" ? "text-purple-300" : "text-rose-300"}`}>
                      {m.pronunciation}
                    </span>
                    <button
                      onClick={() => {
                        const u = new SpeechSynthesisUtterance(m.speech_text);
                        u.lang = "fr-CA";
                        u.rate = 0.95;
                        window.speechSynthesis.cancel();
                        window.speechSynthesis.speak(u);
                      }}
                      data-testid={`speak-${m.name.toLowerCase()}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold ${badgeClass} hover:opacity-90`}
                    >
                      🔊 Écouter
                    </button>
                  </div>
                  <div className="text-[11px] text-zinc-400 mt-2">{m.pronunciation_fr}</div>
                  <div className="text-[10px] text-zinc-500 italic mt-1">{m.pronunciation_en}</div>
                  <div className="text-[11px] text-zinc-300 mt-2 border-t border-zinc-800 pt-2">
                    💡 {m.meaning}
                  </div>
                </div>

                <div className="flex gap-2">
                  <a href={`${BACKEND}/api/marketing-assets/file/${m.file}`} target="_blank" rel="noopener noreferrer"
                    download
                    className={`flex-1 px-3 py-2 rounded-lg ${badgeClass} font-semibold text-xs flex items-center justify-center gap-1`}
                    data-testid={`download-${m.name.toLowerCase()}`}>
                    <Download className="w-3 h-3" /> Télécharger
                  </a>
                  <button onClick={() => copyId(`${BACKEND}/api/marketing-assets/file/${m.file}`)}
                    className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs flex items-center gap-1"
                    data-testid={`copy-url-${m.name.toLowerCase()}`}>
                    <Copy className="w-3 h-3" /> URL
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-950 p-5">
        <h3 className="text-md font-semibold text-amber-200 mb-3">📐 Guidelines d'usage</h3>
        <ul className="space-y-2 text-xs text-zinc-300">
          <li>✅ <strong>Usages OK</strong> : logo dans app, profile Insta/Facebook, hero landing pages, intro/outro Reels, stickers, t-shirts, packaging boutique</li>
          <li>✅ <strong>Vidéos parlantes</strong> : la mascotte apparaît en intro (2 sec), le porte-parole HeyGen pro parle, la mascotte revient en outro avec call-to-action</li>
          <li>❌ <strong>À éviter</strong> : ne PAS modifier les couleurs/proportions sans recadrage, ne PAS utiliser sur fond très occupé sans contour, ne PAS associer aux concurrents</li>
          <li>💡 <strong>Plus tard</strong> : versions animées (Lottie), versions ceintures différentes (jaune/orange/noir), packs stickers pour SMS/WhatsApp</li>
        </ul>
      </div>
    </div>
  );
}
